import type { DirectoryElementsWithPermissions } from '@repo/types/directory';

import { useDeleteDirectories } from '@repo/api/directory/useDeleteDirectories';
import { useDeleteFiles } from '@repo/api/file/useDeleteFiles';
import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { Folders } from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteFSEntry component.
 * @property {DirectoryElementsWithPermissions} elements - Elements to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(open: boolean) => void} onOpenChange - Callback for dialog open state.
 * @property {() => void} [callback] - Optional callback after deletion.
 * @property {string} [directoryId] - Directory ID context for deletion.
 */
interface DeleteFSEntryProps {
  elements: DirectoryElementsWithPermissions;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback?: () => void;
  directoryId?: string;
}

export default function DeleteFSEntry(deleteFSEntryProps: DeleteFSEntryProps) {
  const { elements, open, onOpenChange, callback, directoryId } = deleteFSEntryProps;
  const { executeMutations: directoryMutations } = useDeleteDirectories(directoryId);
  const { executeMutations: filesMutations } = useDeleteFiles(directoryId);

  const onClose = () => {
    onOpenChange(false);
  };

  const onSubmit = async () => {
    const { files, directories } = { ...elements };
    onClose();
    callback?.();
    filesMutations(files.map(({ id }) => id)).catch((error) => {
      toasterMessageHandler(fsErrorMessages, error);
    });
    directoryMutations(directories.map(({ id }) => id)).catch((error) => {
      toasterMessageHandler(fsErrorMessages, error);
    });
  };

  const elementsLen = useMemo<number>(() => {
    return elements.directories.length + elements.files.length;
  }, [elements]);

  const elementsNames = useMemo<string[]>(() => {
    return [...elements.directories, ...elements.files].map((element) => element.name);
  }, [elements]);

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader className={'border-border-primary bg-card border-b'}>
            <FeaturedIcon size={'lg'}>
              <Folders />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Usunąć {'( ' + elementsLen + ' )'} ?</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Usuń wybrane elementy</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-x-hidden p-4'}>
            <p className="text-muted-foreground text-sm">Czy na pewno chcesz usunąć wybrane elementy ?</p>
            <div className={'flex flex-wrap gap-2 overflow-auto'}>
              {elementsNames.map((name, idx) => (
                <Badge key={name + '_' + idx} variant={'muted'} size={'sm'} className="max-w-full">
                  <span className="truncate">{name}</span>
                </Badge>
              ))}
            </div>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
